import React, { useRef, useState } from 'react'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import KeyboardBackspaceIcon from 'components/material-symbol/icons/KeyboardBackspace'
import { useFormContext } from 'react-hook-form'
import ReCAPTCHA from 'react-google-recaptcha'
import { RECAPTCHA_V2_KEY } from 'env'
import { getRegistrationButtonLabel } from '../registration-cta-utils'
import { useRegistrationInvite } from '../registration-invite'
import useStyles from './group-event-registration-form.styles'

export default function StepActions({
  stepName,
  setActiveStep,
  registrationDisabled = false,
  onSubmit,
  user,
  groupEvent,
  waiverEnabled = false,
  verifyFirstTimeParticipant,
}) {
  const [isCheckingParticipation, setIsCheckingParticipation] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState(null)
  const [recaptchaUnavailable, setRecaptchaUnavailable] = useState(false)
  const recaptchaRef = useRef(null)
  const form = useFormContext()
  const classes = useStyles()
  const { isValid: inviteValid } = useRegistrationInvite()
  const agreeWaiver = form.watch('agreeWaiver')
  if (stepName === 'Confirmation') return null

  const continueLabel = getRegistrationButtonLabel({
    user,
    groupEvent,
    registerLabel: 'Continue',
    inviteValid,
  })

  const requiresRecaptcha =
    Boolean(verifyFirstTimeParticipant) && !user && Boolean(RECAPTCHA_V2_KEY)
  // A blocked script or a widget error must not stop a golfer from continuing.
  // Without a widget the token stays null, and the backend rejects the lookup,
  // which the form treats as a fail-open.
  const showRecaptcha = requiresRecaptcha && !recaptchaUnavailable

  const handleContinueClick = async () => {
    const isValid = await form.trigger(['student', 'emergencyContact'])
    if (!isValid) return null

    setIsCheckingParticipation(true)
    try {
      const canContinue = verifyFirstTimeParticipant
        ? await verifyFirstTimeParticipant({
          email: form.getValues('student.email'),
          dateOfBirth: form.getValues('student.dateOfBirth'),
          recaptchaToken,
        })
        : true
      return canContinue ? setActiveStep(1) : null
    } finally {
      // reCAPTCHA tokens are single use, so every check needs a fresh one.
      recaptchaRef.current?.reset()
      setRecaptchaToken(null)
      setIsCheckingParticipation(false)
    }
  }
  const handleAdditionalInfoContinueClick = async () => {
    const isValid = await form.trigger('pgaHopeProfile')
    return isValid ? setActiveStep(2) : null
  }
  const handleSubmit = async () => {
    const isValid = await form.trigger()
    if (isValid) {
      await form.handleSubmit(onSubmit)()
    }
  }

  if (stepName === 'Participant Information') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        pb={5}
        className={classes.stepActions}
      >
        {showRecaptcha && (
          <Box mb={2}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_V2_KEY}
              onChange={token => setRecaptchaToken(token)}
              onExpired={() => setRecaptchaToken(null)}
              onErrored={() => setRecaptchaUnavailable(true)}
              asyncScriptOnLoad={({ errored }) => {
                if (errored) setRecaptchaUnavailable(true)
              }}
            />
          </Box>
        )}
        <Button
          variant="contained"
          className={`${classes.actionButton} ${classes.next}`}
          onClick={handleContinueClick}
          disabled={
            registrationDisabled ||
            isCheckingParticipation ||
            (showRecaptcha && !recaptchaToken)
          }
        >
          {continueLabel}
        </Button>
      </Box>
    )
  }
  if (stepName === 'Additional Information') {
    return (
      <Box
        display="flex"
        justifyContent="space-evenly"
        alignItems="flex-start"
        pb={5}
        className={classes.stepActions}
      >
        <Button
          startIcon={<KeyboardBackspaceIcon />}
          className={classes.previous}
          onClick={() => setActiveStep(0)}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          className={`${classes.actionButton} ${classes.next}`}
          onClick={
            waiverEnabled ? handleAdditionalInfoContinueClick : handleSubmit
          }
        >
          {waiverEnabled ? 'Continue' : 'Complete Registration'}
        </Button>
      </Box>
    )
  }
  if (stepName === 'Waiver') {
    return (
      <Box
        display="flex"
        justifyContent="space-evenly"
        alignItems="flex-start"
        pb={5}
        className={classes.stepActions}
      >
        <Button
          startIcon={<KeyboardBackspaceIcon />}
          className={classes.previous}
          onClick={() => setActiveStep(1)}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          className={`${classes.actionButton} ${classes.next}`}
          onClick={handleSubmit}
          disabled={!agreeWaiver}
        >
          Complete Registration
        </Button>
      </Box>
    )
  }
  return null
}
